/**
 * Procedural artwork — the default "glyph" pack (ADR-009, docs/11-ux-ui.md §2).
 * Everything is derived from stable seeds (item id, emoji id), so the same
 * item always looks the same, offline, with no third-party art.
 */
import { useId } from 'react';
import { getItem, type ItemType } from '@fakerpg/core';
import { CircleDot, Flame, Droplet, Mountain, Sparkles, Wind, type LucideIcon } from 'lucide-react';
import { ELEMENT_COLORS, ELEMENT_HUES, tierStyle } from './palette.js';
import { initials, seededRandom, smoothClosedPath } from './seed.js';
import type { UiIcon } from './keys.js';

// ── Type emblems (100×100 space) ─────────────────────────────────
// Silhouettes come from the item's tags (weapon kind, character class), so
// items of the same type are told apart at a glance. Accents use the element color.

const BLADE = 'M50 12 L57 23 L56 60 L44 60 L43 23 Z';

function WeaponShape({ kind, accent }: { kind: string; accent: string }) {
  switch (kind) {
    case 'axe':
      return (
        <g>
          <rect x="46.5" y="18" width="7" height="68" rx="3" />
          <path d="M53 20 C72 16 80 32 74 50 C67 44 60 42 53 44 Z" />
          <path d="M47 24 C36 22 30 30 32 40 C37 37 42 36 47 37 Z" opacity="0.85" />
          <circle cx="50" cy="88" r="4" fill={accent} />
        </g>
      );
    case 'hammer':
      return (
        <g>
          <rect x="46.5" y="30" width="7" height="58" rx="3" />
          <rect x="28" y="14" width="44" height="22" rx="5" />
          <rect x="28" y="22" width="44" height="5" fill={accent} />
        </g>
      );
    case 'staff':
      return (
        <g>
          <rect x="47" y="26" width="6" height="64" rx="3" />
          <circle cx="50" cy="20" r="11" fill={accent} />
          <circle cx="46" cy="16" r="3.5" fill="#fff" opacity="0.8" />
          <path d="M38 30 Q50 22 62 30 L60 34 Q50 28 40 34 Z" />
        </g>
      );
    case 'wand':
      return (
        <g>
          <rect x="47.5" y="38" width="5" height="48" rx="2.5" transform="rotate(18 50 62)" />
          <path d="M44 14 L48 25 L60 25 L50 32 L54 44 L44 36 L34 44 L38 32 L28 25 L40 25 Z" fill={accent} />
        </g>
      );
    case 'bow':
      return (
        <g>
          <path d="M40 12 C66 26 66 74 40 88 L44 90 C72 74 72 26 44 10 Z" />
          <path d="M42 11 L42 89" stroke="#fff" strokeWidth="1.6" fill="none" />
          <path d="M24 50 H66 M60 44 L68 50 L60 56" stroke={accent} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      );
    case 'dagger':
      return (
        <g>
          <path d="M50 22 L56 30 L55 58 L45 58 L44 30 Z" />
          <path d="M34 57 H66 V63 H34 Z" />
          <rect x="46.5" y="63" width="7" height="14" rx="2" />
          <circle cx="50" cy="81" r="4.5" fill={accent} />
        </g>
      );
    case 'shovel':
      return (
        <g>
          <rect x="47" y="12" width="6" height="52" rx="3" />
          <path d="M40 12 H60 V18 H40 Z" />
          <path d="M36 62 H64 V76 C64 84 56 90 50 90 C44 90 36 84 36 76 Z" />
          <rect x="47" y="64" width="6" height="8" fill={accent} />
        </g>
      );
    default:
      return (
        <g>
          <path d={BLADE} />
          <path d="M50 18 L50 58" stroke="rgba(0,0,0,.18)" strokeWidth="2" fill="none" />
          <path d="M31 59 Q50 66 69 59 L69 66 Q50 72 31 66 Z" />
          <rect x="46.5" y="67" width="7" height="14" rx="2" />
          <circle cx="50" cy="85" r="5.5" fill={accent} />
        </g>
      );
  }
}

/** Head-gear per class; drawn over the bust, filled with the element accent. */
function Headgear({ kind, accent }: { kind: string; accent: string }) {
  const p = { fill: accent };
  switch (kind) {
    case 'iop':
      return <path {...p} d="M30 34 C30 18 40 12 50 12 C60 12 70 18 70 34 L64 30 L60 20 L56 28 L50 6 L44 28 L40 20 L36 30 Z" />;
    case 'cra':
      return (
        <g {...p}>
          <path d="M27 44 C26 22 38 10 50 10 C62 10 74 22 73 44 L66 42 C65 28 58 21 50 21 C42 21 35 28 34 42 Z" />
          <path d="M66 18 Q84 6 80 28 Q74 22 68 26 Z" />
        </g>
      );
    case 'sadida':
      return (
        <g {...p}>
          <ellipse cx="38" cy="16" rx="6" ry="12" transform="rotate(-30 38 16)" />
          <ellipse cx="50" cy="11" rx="6" ry="12" />
          <ellipse cx="62" cy="16" rx="6" ry="12" transform="rotate(30 62 16)" />
        </g>
      );
    case 'enutrof':
      return (
        <g {...p}>
          <path d="M20 30 Q50 20 80 30 L80 35 Q50 27 20 35 Z" />
          <path d="M35 30 Q37 10 50 10 Q63 10 65 30 Z" />
        </g>
      );
    case 'eliotrope':
      return (
        <g {...p}>
          <path d="M30 36 C30 20 40 12 50 12 C60 12 70 20 70 36 L64 32 C62 24 56 21 50 21 C44 21 38 24 36 32 Z" />
          <path d="M30 26 L14 16 L24 32 Z" />
          <path d="M70 26 L86 16 L76 32 Z" />
        </g>
      );
    case 'sram':
      return (
        <g>
          <path fill="#2a2433" d="M27 46 C26 22 38 10 50 10 C62 10 74 22 73 46 L67 44 C66 30 59 22 50 22 C41 22 34 30 33 44 Z" />
          <circle cx="44" cy="17" r="2" fill={accent} />
          <circle cx="56" cy="17" r="2" fill={accent} />
        </g>
      );
    case 'osamodas':
      return (
        <g {...p}>
          <path d="M34 24 C24 18 20 8 26 4 C26 12 32 16 38 18 Z" />
          <path d="M66 24 C76 18 80 8 74 4 C74 12 68 16 62 18 Z" />
        </g>
      );
    case 'feca':
      return (
        <g {...p}>
          <path d="M30 36 C30 18 40 12 50 12 C60 12 70 18 70 36 L66 36 L66 32 L34 32 L34 36 Z" />
          <path d="M44 4 L56 4 L50 14 Z" />
        </g>
      );
    case 'pandawa':
      return (
        <g {...p}>
          <circle cx="32" cy="18" r="8" />
          <circle cx="68" cy="18" r="8" />
        </g>
      );
    case 'ecaflip':
      return (
        <g {...p}>
          <path d="M30 26 L32 4 L46 18 Z" />
          <path d="M70 26 L68 4 L54 18 Z" />
        </g>
      );
    case 'eniripsa':
      return <ellipse cx="50" cy="8" rx="15" ry="4" fill="none" stroke={accent} strokeWidth="3.5" />;
    case 'xelor':
      return (
        <g fill="none" stroke={accent} strokeWidth="3.5" strokeLinecap="round">
          <path d="M33 26 Q50 20 67 26" />
          <path d="M31 34 Q50 28 69 34" />
          <circle cx="50" cy="10" r="6" fill={accent} stroke="none" />
        </g>
      );
    case 'sacrieur':
    case 'sacrier':
      return <path {...p} d="M32 30 L28 12 L38 22 L42 6 L48 20 L54 4 L58 20 L66 8 L66 24 L72 18 L68 32 Z" />;
    case 'huppermage':
      return (
        <g {...p}>
          <path d="M22 28 Q50 20 78 28 L78 32 Q50 25 22 32 Z" />
          <path d="M36 27 L58 0 L64 27 Z" />
        </g>
      );
    case 'multi':
      return <path {...p} d="M32 26 L36 10 L43 20 L50 6 L57 20 L64 10 L68 26 Z" />;
    default:
      return null;
  }
}

const CHARACTER_CLASSES = ['iop', 'cra', 'sadida', 'enutrof', 'eliotrope', 'sram', 'osamodas', 'feca', 'pandawa', 'ecaflip', 'eniripsa', 'xelor', 'sacrieur', 'sacrier', 'huppermage', 'multi'];
const WEAPON_KINDS = ['sword', 'axe', 'hammer', 'staff', 'wand', 'bow', 'dagger', 'shovel'];

function ShieldShape({ seed, accent }: { seed: number; accent: string }) {
  const shape = seed % 3;
  const mark = Math.floor(seed / 3) % 4;
  const outline =
    shape === 0
      ? 'M50 15 L78 24 V46 C78 66 65 79 50 86 C35 79 22 66 22 46 V24 Z'
      : shape === 1
        ? 'M50 14 A35 35 0 1 1 49.9 14 Z'
        : 'M50 10 L74 22 C76 48 66 74 50 90 C34 74 24 48 26 22 Z';
  return (
    <g>
      <path d={outline} />
      <path d={outline} fill="rgba(0,0,0,.12)" transform="translate(50 50) scale(0.78) translate(-50 -50)" />
      {mark === 0 && <path d="M50 30 V72 M34 48 H66" stroke={accent} strokeWidth="6" strokeLinecap="round" fill="none" />}
      {mark === 1 && <path d="M50 30 L55 44 L70 44 L58 53 L62 68 L50 59 L38 68 L42 53 L30 44 L45 44 Z" fill={accent} />}
      {mark === 2 && <path d="M32 42 L50 58 L68 42 M32 54 L50 70 L68 54" stroke={accent} strokeWidth="5" strokeLinejoin="round" fill="none" />}
      {mark === 3 && <circle cx="50" cy="50" r="12" fill={accent} />}
    </g>
  );
}

function Emblem({ type, id, tags, accent }: { type: ItemType; id: number; tags: string[]; accent: string }) {
  switch (type) {
    case 'weapons':
      return <WeaponShape kind={tags.find((t) => WEAPON_KINDS.includes(t)) ?? 'sword'} accent={accent} />;
    case 'shields':
      return <ShieldShape seed={id * 7 + 3} accent={accent} />;
    case 'pets':
      return null;
    case 'characters':
      return (
        <g>
          <path d="M50 16 C38 16 31 26 31 37 C31 50 39 58 50 58 C61 58 69 50 69 37 C69 26 62 16 50 16 Z" />
          <path d="M22 88 C22 70 34 60 50 60 C66 60 78 70 78 88 Z" />
          <path d="M36 62 Q50 72 64 62 L62 68 Q50 76 38 68 Z" fill={accent} />
          <circle cx="43" cy="39" r="2.4" fill="#2a2433" />
          <circle cx="57" cy="39" r="2.4" fill="#2a2433" />
          <Headgear kind={tags.find((t) => CHARACTER_CLASSES.includes(t)) ?? ''} accent={accent} />
        </g>
      );
  }
}

function Sigil({ seed, tier }: { seed: string; tier: number }) {
  const rnd = seededRandom(seed);
  const sides = 3 + Math.floor(rnd() * 6);
  const rot = rnd() * 360;
  const star: string[] = [];
  for (let i = 0; i < sides * 2; i++) {
    const r = i % 2 === 0 ? 41 : 30 + rnd() * 6;
    const a = (Math.PI * i) / sides + (rot * Math.PI) / 180;
    star.push(`${(50 + r * Math.cos(a)).toFixed(1)},${(50 + r * Math.sin(a)).toFixed(1)}`);
  }
  const dots = 6 + Math.floor(rnd() * 7);
  return (
    <g className="art__sigil" fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="1.2">
      {tier >= 5 && (
        <g className="art__rays" opacity="0.5">
          {Array.from({ length: 16 }, (_, i) => (
            <path key={i} d="M50 50 L48.5 2 L51.5 2 Z" fill="rgba(255,255,255,.18)" stroke="none" transform={`rotate(${(360 / 16) * i} 50 50)`} />
          ))}
        </g>
      )}
      <circle cx="50" cy="50" r="44" strokeDasharray={`${2 + rnd() * 4} ${2 + rnd() * 5}`} />
      <polygon points={star.join(' ')} />
      <circle cx="50" cy="50" r={18 + rnd() * 8} />
      {Array.from({ length: dots }, (_, i) => {
        const a = (2 * Math.PI * i) / dots + rot;
        return <circle key={i} cx={50 + 44 * Math.cos(a)} cy={50 + 44 * Math.sin(a)} r="1.6" fill="rgba(255,255,255,.4)" stroke="none" />;
      })}
    </g>
  );
}

/** Square item art: tier gradient, seeded sigil, type emblem, monogram. */
export function ItemArt({ type, id, size, masked = false, hideEmblem = false }: { type: ItemType; id: number; size: number; masked?: boolean; hideEmblem?: boolean }) {
  const uid = useId().replace(/:/g, '');
  const item = getItem(type, id);
  const tier = item?.tier ?? 1;
  const style = masked ? { base: '#5b6170', light: '#8a90a0', dark: '#2a2e38' } : tierStyle(tier);
  const elem = ELEMENT_COLORS[item?.element ?? 'Neutral'] ?? '#999';
  const mono = masked ? '?' : initials(item?.name.en ?? '?');
  return (
    <svg className="art" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <radialGradient id={`bg${uid}`} cx="50%" cy="38%" r="75%">
          <stop offset="0" stopColor={style.light} />
          <stop offset="0.55" stopColor={style.base} />
          <stop offset="1" stopColor={style.dark} />
        </radialGradient>
        <linearGradient id={`em${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor={masked ? '#c7cad3' : '#f3e7d0'} />
        </linearGradient>
        <filter id={`sh${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="2.2" floodColor="#000" floodOpacity="0.45" />
        </filter>
      </defs>
      <rect width="100" height="100" fill={`url(#bg${uid})`} />
      {!masked && <Sigil seed={`${type}:${id}`} tier={tier} />}
      {!masked && <circle cx="50" cy="50" r="30" fill={elem} opacity="0.22" />}
      {!hideEmblem && type === 'pets' && !masked && (
        <g filter={`url(#sh${uid})`} transform="translate(50 50) scale(0.86) translate(-50 -52)">
          <Creature seed={`pet:${id}`} element={item?.element ?? 'Neutral'} boss={tier >= 6} uid={`p${uid}`} />
        </g>
      )}
      {!hideEmblem && (type !== 'pets' || masked) && (
        <g fill={masked ? '#1d2029' : `url(#em${uid})`} filter={`url(#sh${uid})`} opacity={masked ? 0.85 : 1}>
          {masked && type === 'pets' ? (
            <path d="M50 26 C68 26 78 40 78 56 C78 72 66 82 50 82 C34 82 22 72 22 56 C22 40 32 26 50 26 Z" />
          ) : (
            <Emblem type={type} id={id} tags={item?.tags ?? []} accent={masked ? '#1d2029' : elem} />
          )}
        </g>
      )}
      <g>
        <rect x="33" y="84" width="34" height="12" rx="6" fill="rgba(0,0,0,.42)" />
        <text x="50" y="93.2" textAnchor="middle" fontSize="8.5" fontWeight="800" letterSpacing="1" fill="#fff" fontFamily="Inter Variable, system-ui, sans-serif">
          {mono}
        </text>
      </g>
    </svg>
  );
}

// ── Monsters & speakers ──────────────────────────────────────────

interface CreatureOptions {
  seed: string;
  element: string;
  boss: boolean;
  uid: string;
}

/** Body, eyes, mouth and extras of a seeded creature, centered on (50, 56). */
function Creature({ seed, element, boss, uid }: CreatureOptions) {
  const rnd = seededRandom(seed);
  const baseHue = element === 'Neutral' ? Math.floor(rnd() * 360) : (ELEMENT_HUES[element] ?? 230) + Math.floor(rnd() * 60) - 30;
  const hue = (baseHue + 360) % 360;
  const sat = element === 'Neutral' ? 35 + rnd() * 25 : 50 + rnd() * 25;
  const body = `hsl(${hue} ${sat.toFixed(0)}% ${(54 + rnd() * 10).toFixed(0)}%)`;
  const bodyDark = `hsl(${hue} ${sat.toFixed(0)}% 28%)`;
  const n = 9 + Math.floor(rnd() * 5);
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (2 * Math.PI * i) / n - Math.PI / 2;
    const r = 30 * (0.82 + rnd() * 0.3);
    pts.push([50 + r * Math.cos(a), 56 + r * 0.92 * Math.sin(a)]);
  }
  const eyes = rnd() < 0.18 ? 1 : rnd() < 0.82 ? 2 : 3;
  const eyeR = 6 + rnd() * 3.5;
  const eyeY = 48 + rnd() * 4;
  const spread = eyes === 1 ? [0] : eyes === 2 ? [-11, 11] : [-15, 0, 15];
  const look = rnd() * 2 - 1;
  const mouth = Math.floor(rnd() * 3);
  const hornRoll = rnd();
  const horns = boss || hornRoll < 0.32;
  const hornA = rnd();
  const hornB = rnd();
  const spots = rnd() < 0.5 ? 2 + Math.floor(rnd() * 3) : 0;
  const spotPos = Array.from({ length: spots }, () => [34 + rnd() * 32, 66 + rnd() * 12, 2.5 + rnd() * 3] as const);
  const path = smoothClosedPath(pts);
  return (
    <g>
      <defs>
        <radialGradient id={`mb${uid}`} cx="40%" cy="35%" r="70%">
          <stop offset="0" stopColor="#fff" stopOpacity="0.4" />
          <stop offset="0.5" stopColor={body} stopOpacity="0" />
        </radialGradient>
      </defs>
      {horns && (
        <g fill={bodyDark}>
          <path d={`M33 ${36 - hornA * 4} L24 ${12 + hornB * 8} L42 30 Z`} />
          <path d={`M67 ${36 - hornA * 4} L76 ${12 + hornB * 8} L58 30 Z`} />
        </g>
      )}
      <path d={path} fill={body} stroke={bodyDark} strokeWidth="2.5" />
      <path d={path} fill={`url(#mb${uid})`} />
      {spotPos.map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r} fill="rgba(255,255,255,.22)" />
      ))}
      {spread.map((dx, i) => (
        <g key={i}>
          <circle cx={50 + dx} cy={eyeY} r={eyeR} fill="#fff" stroke={bodyDark} strokeWidth="1.5" />
          <circle cx={50 + dx + look * 2} cy={eyeY + 1} r={eyeR * 0.48} fill="#1b1d2a" />
          <circle cx={50 + dx + look * 2 - 1.2} cy={eyeY - 0.8} r={eyeR * 0.16} fill="#fff" />
        </g>
      ))}
      {mouth === 0 && <path d="M40 68 Q50 76 60 68" stroke="#1b1d2a" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
      {mouth === 1 && (
        <g>
          <ellipse cx="50" cy="70" rx="9" ry="6" fill="#3a1420" />
          <path d="M43 66 L46 70 L49 66 M51 66 L54 70 L57 66" fill="#fff" />
        </g>
      )}
      {mouth === 2 && <path d="M39 69 L44 66 L48 70 L52 66 L56 70 L61 67" stroke="#1b1d2a" strokeWidth="2.2" fill="none" strokeLinejoin="round" />}
      {boss && <path d="M36 22 L41 10 L46 18 L50 6 L54 18 L59 10 L64 22 Z" fill="#f0b44c" stroke="#7a4a10" strokeWidth="1.5" />}
    </g>
  );
}

/** A seeded little creature (monsters, NPC speakers). */
export function MonsterArt({ seed, element = 'Neutral', size, boss = false }: { seed: string; element?: string; size: number; boss?: boolean }) {
  const uid = useId().replace(/:/g, '');
  return (
    <svg className="art art--monster" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <radialGradient id={`mg${uid}`} cx="50%" cy="50%" r="60%">
          <stop offset="0" stopColor={ELEMENT_COLORS[element] ?? '#999'} stopOpacity="0.45" />
          <stop offset="1" stopColor={ELEMENT_COLORS[element] ?? '#999'} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="52" r="48" fill={`url(#mg${uid})`} />
      <ellipse cx="50" cy="88" rx="26" ry="5" fill="rgba(0,0,0,.25)" />
      <Creature seed={seed} element={element} boss={boss} uid={uid} />
    </svg>
  );
}

// ── Currency & stat icons ────────────────────────────────────────

export function CurrencyIcon({ name, size }: { name: UiIcon; size: number }) {
  const uid = useId().replace(/:/g, '');
  const g = (a: string, b: string) => (
    <linearGradient id={`c${uid}`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor={a} />
      <stop offset="1" stopColor={b} />
    </linearGradient>
  );
  const fill = `url(#c${uid})`;
  const body = (() => {
    switch (name) {
      case 'fakecoin':
        return (
          <>
            <defs>{g('#ffe08a', '#d9901f')}</defs>
            <circle cx="32" cy="32" r="27" fill={fill} stroke="#8a5a12" strokeWidth="3" />
            <circle cx="32" cy="32" r="19" fill="none" stroke="rgba(255,255,255,.55)" strokeWidth="2" />
            <path d="M26 20 H40 V25 H31.5 V30 H38 V35 H31.5 V44 H26 Z" fill="#8a5a12" />
          </>
        );
      case 'lootcoin':
        return (
          <>
            <defs>{g('#d3b8ff', '#6b3fe0')}</defs>
            <path d="M18 8 H46 L58 24 L32 58 L6 24 Z" fill={fill} stroke="#3c1f8f" strokeWidth="3" strokeLinejoin="round" />
            <path d="M6 24 H58 M18 8 L26 24 L32 58 L38 24 L46 8" fill="none" stroke="rgba(255,255,255,.5)" strokeWidth="2" strokeLinejoin="round" />
          </>
        );
      case 'gems':
        return (
          <>
            <defs>{g('#bff6ff', '#1b9fbf')}</defs>
            <path d="M32 4 L56 28 L32 60 L8 28 Z" fill={fill} stroke="#0d5a6e" strokeWidth="3" strokeLinejoin="round" />
            <path d="M8 28 H56 M32 4 L22 28 L32 60 L42 28 Z" fill="none" stroke="rgba(255,255,255,.55)" strokeWidth="2" strokeLinejoin="round" />
          </>
        );
      case 'energy':
        return (
          <>
            <defs>{g('#fff3a0', '#f0a920')}</defs>
            <path d="M38 3 L12 36 H29 L24 61 L52 25 H35 Z" fill={fill} stroke="#8a5a00" strokeWidth="3" strokeLinejoin="round" />
          </>
        );
      case 'xp':
        return (
          <>
            <defs>{g('#d9ffad', '#4fae2c')}</defs>
            <path d="M32 4 L40 23 L60 24 L44 37 L50 57 L32 46 L14 57 L20 37 L4 24 L24 23 Z" fill={fill} stroke="#2b6b14" strokeWidth="3" strokeLinejoin="round" />
          </>
        );
      case 'attack':
        return (
          <>
            <defs>{g('#ffd2c4', '#e0563a')}</defs>
            <path d="M54 4 H60 V10 L30 40 L24 34 Z" fill={fill} stroke="#7a2412" strokeWidth="3" strokeLinejoin="round" />
            <path d="M14 36 L28 50 M20 30 L34 44" stroke="#7a2412" strokeWidth="5" strokeLinecap="round" />
            <path d="M18 46 L8 56" stroke="#7a2412" strokeWidth="6" strokeLinecap="round" />
          </>
        );
      case 'hp':
        return (
          <>
            <defs>{g('#ffb3c6', '#e03562')}</defs>
            <path d="M32 57 L9 34 C1 26 5 9 19 9 C25 9 29 13 32 17 C35 13 39 9 45 9 C59 9 63 26 55 34 Z" fill={fill} stroke="#7a1430" strokeWidth="3" strokeLinejoin="round" />
            <path d="M16 20 C18 16 22 15 25 16" stroke="rgba(255,255,255,.7)" strokeWidth="3" fill="none" strokeLinecap="round" />
          </>
        );
      case 'level':
        return (
          <>
            <defs>{g('#bcdcff', '#3d7fe0')}</defs>
            <path d="M32 4 L58 30 H42 V60 H22 V30 H6 Z" fill={fill} stroke="#1a3f7a" strokeWidth="3" strokeLinejoin="round" />
          </>
        );
      case 'pass':
        return (
          <>
            <defs>{g('#ffe6b0', '#e08a2a')}</defs>
            <path d="M6 18 H58 V27 A5 5 0 0 0 58 37 V46 H6 V37 A5 5 0 0 0 6 27 Z" fill={fill} stroke="#7a420d" strokeWidth="3" strokeLinejoin="round" />
            <path d="M22 19 V45" stroke="#7a420d" strokeWidth="2.5" strokeDasharray="3 4" />
            <path d="M39 24 L41.8 30 L48 30.6 L43.3 34.8 L44.7 41 L39 37.7 L33.3 41 L34.7 34.8 L30 30.6 L36.2 30 Z" fill="#fff6dc" />
          </>
        );
      case 'nugget':
        return (
          <>
            <defs>{g('#fff1a8', '#d6a31a')}</defs>
            <path d="M12 40 L20 20 L36 12 L52 22 L56 40 L42 54 L22 52 Z" fill={fill} stroke="#7a5a0a" strokeWidth="3" strokeLinejoin="round" />
            <path d="M20 20 L30 32 L36 12 M30 32 L42 54 M30 32 L56 40" stroke="rgba(255,255,255,.55)" strokeWidth="2" fill="none" />
          </>
        );
      case 'orb':
        return (
          <>
            <defs>
              <radialGradient id={`c${uid}`} cx="0.35" cy="0.3" r="0.8">
                <stop offset="0" stopColor="#f2e6ff" />
                <stop offset="0.45" stopColor="#a77bff" />
                <stop offset="1" stopColor="#4b20b8" />
              </radialGradient>
            </defs>
            <circle cx="32" cy="32" r="26" fill={fill} stroke="#2e1275" strokeWidth="3" />
            <path d="M14 34 C22 24 42 24 50 34" stroke="rgba(255,255,255,.6)" strokeWidth="2.5" fill="none" />
            <circle cx="24" cy="22" r="5" fill="rgba(255,255,255,.75)" />
          </>
        );
      case 'icekamas':
        return (
          <>
            <defs>{g('#e6fbff', '#58b9e6')}</defs>
            <circle cx="32" cy="32" r="27" fill={fill} stroke="#1d5f80" strokeWidth="3" />
            <path d="M32 14 V50 M16.4 23 L47.6 41 M16.4 41 L47.6 23" stroke="#1d5f80" strokeWidth="3.5" strokeLinecap="round" />
          </>
        );
      case 'page':
        return (
          <>
            <defs>{g('#fff8e6', '#e6c98a')}</defs>
            <path d="M14 6 H42 L52 16 V58 H14 Z" fill={fill} stroke="#7a5a26" strokeWidth="3" strokeLinejoin="round" />
            <path d="M42 6 V16 H52 M21 26 H45 M21 34 H45 M21 42 H37" stroke="#7a5a26" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          </>
        );
      case 'legendary':
        return (
          <>
            <defs>{g('#ffe9a8', '#e0731a')}</defs>
            <circle cx="32" cy="32" r="27" fill={fill} stroke="#7a3a0a" strokeWidth="3" />
            <path d="M18 40 L20 24 L27 31 L32 20 L37 31 L44 24 L46 40 Z" fill="#7a3a0a" />
          </>
        );
    }
  })();
  return (
    <svg className="art art--icon" width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      {body}
    </svg>
  );
}

// ── Dofus eggs ───────────────────────────────────────────────────

const DOFUS_COLORS: Record<string, [string, string, string]> = {
  one: ['#9cf0b8', '#2fae5f', '#145c31'],
  two: ['#ffb0b8', '#d33a4a', '#6e1420'],
  three: ['#b0f6f2', '#24b3b0', '#0d5654'],
  four: ['#ffd9a0', '#d9902a', '#6e430d'],
  five: ['#fffaf0', '#e8dcc0', '#8a7a55'],
  six: ['#6e5a8a', '#2b2236', '#0d0912'],
};

/** A Dofus egg: tier colours, or a seeded egg for the roulette's "miss" symbols. */
export function DofusArt({ tier, seed = '', size, dim = false }: { tier?: string; seed?: string; size: number; dim?: boolean }) {
  const uid = useId().replace(/:/g, '');
  const rand = seededRandom(`dofus:${tier ?? seed}`);
  const hue = Math.floor(rand() * 360);
  const [light, mid, dark] = (tier && DOFUS_COLORS[tier]) || [`hsl(${hue} 70% 82%)`, `hsl(${hue} 55% 52%)`, `hsl(${hue} 50% 24%)`];
  const spots = Array.from({ length: 4 }, () => ({ x: 22 + rand() * 20, y: 22 + rand() * 26, r: 2 + rand() * 3.5 }));
  return (
    <svg className={`art art--dofus ${dim ? 'is-dim' : ''}`} width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <radialGradient id={`d${uid}`} cx="0.36" cy="0.3" r="0.85">
          <stop offset="0" stopColor={light} />
          <stop offset="0.55" stopColor={mid} />
          <stop offset="1" stopColor={dark} />
        </radialGradient>
      </defs>
      <path d="M32 5 C46 5 54 24 54 38 C54 51 44 59 32 59 C20 59 10 51 10 38 C10 24 18 5 32 5 Z" fill={`url(#d${uid})`} stroke={dark} strokeWidth="2.5" />
      {spots.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={s.r} fill={dark} opacity="0.35" />
      ))}
      <ellipse cx="24" cy="18" rx="5" ry="8" fill="#fff" opacity="0.45" transform="rotate(-20 24 18)" />
      {tier === 'six' && <path d="M32 14 L35 24 L45 24 L37 30 L40 40 L32 34 L24 40 L27 30 L19 24 L29 24 Z" fill="#f0b44c" opacity="0.9" />}
    </svg>
  );
}

// ── Arena leagues ────────────────────────────────────────────────

const LEAGUE_ART: [string, string, string][] = [
  ['#f2c39a', '#c98a4b', '#6e4320'],
  ['#f4f7fb', '#b8c2cf', '#5a6577'],
  ['#ffe6a8', '#f0b44c', '#8a5a12'],
  ['#c8fff6', '#5fd3c0', '#1d6e62'],
  ['#e0ecff', '#8fb8ff', '#2e4f8f'],
  ['#f0d6ff', '#c56bff', '#5a1f8a'],
];

/** League crest: ribbon + medal in the league's metal; the legend league gets a crown. */
export function LeagueArt({ rank, size }: { rank: number; size: number }) {
  const uid = useId().replace(/:/g, '');
  const [light, mid, dark] = LEAGUE_ART[Math.min(Math.max(rank, 0), LEAGUE_ART.length - 1)]!;
  return (
    <svg className="art art--league" width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <radialGradient id={`l${uid}`} cx="0.38" cy="0.32" r="0.8">
          <stop offset="0" stopColor={light} />
          <stop offset="0.6" stopColor={mid} />
          <stop offset="1" stopColor={dark} />
        </radialGradient>
      </defs>
      <path d="M20 4 H30 L36 22 H26 Z M44 4 H34 L28 22 H38 Z" fill={dark} opacity="0.85" />
      <circle cx="32" cy="38" r="21" fill={`url(#l${uid})`} stroke={dark} strokeWidth="3" />
      <circle cx="32" cy="38" r="14" fill="none" stroke="#fff" strokeOpacity="0.55" strokeWidth="2" />
      {rank >= 5 ? (
        <path d="M22 44 L23.5 32 L28.5 37 L32 29 L35.5 37 L40.5 32 L42 44 Z" fill={dark} />
      ) : (
        Array.from({ length: rank + 1 }, (_, i) => (
          <circle key={i} cx={32 + (i - rank / 2) * 6} cy="38" r="2.6" fill={dark} />
        ))
      )}
    </svg>
  );
}

// ── Chests ───────────────────────────────────────────────────────

const CHEST_COLORS: Record<string, { wood: string; dark: string; band: string; glow?: string }> = {
  wood: { wood: '#b57a43', dark: '#6e4320', band: '#8c8c96' },
  silver: { wood: '#8b97a8', dark: '#4d5666', band: '#e4e9f2' },
  gold: { wood: '#a3622e', dark: '#5c3311', band: '#f0b44c', glow: '#f0b44c' },
  enchanted: { wood: '#6a4fe0', dark: '#2e1f78', band: '#c8b6ff', glow: '#8b6cff' },
  dofus: { wood: '#3f8f5a', dark: '#1d4a2c', band: '#ffe08a', glow: '#4fb97a' },
  mythic: { wood: '#1f9fbf', dark: '#0d4a5c', band: '#bff6ff', glow: '#3fd0f0' },
  shushu: { wood: '#c0392b', dark: '#5c1610', band: '#f6d365', glow: '#ff6b5a' },
};

/** Procedural treasure chest; `open` lifts the lid (reveal animations). */
export function ChestArt({ name, size, open = false }: { name: string; size: number; open?: boolean }) {
  const c = CHEST_COLORS[name] ?? CHEST_COLORS['wood']!;
  const uid = useId().replace(/:/g, '');
  return (
    <svg className={`art art--chest ${open ? 'is-open' : ''}`} width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={`w${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c.wood} />
          <stop offset="1" stopColor={c.dark} />
        </linearGradient>
        {c.glow && (
          <radialGradient id={`g${uid}`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor={c.glow} stopOpacity="0.55" />
            <stop offset="1" stopColor={c.glow} stopOpacity="0" />
          </radialGradient>
        )}
      </defs>
      {c.glow && <circle cx="32" cy="34" r="31" fill={`url(#g${uid})`} />}
      <rect x="8" y="30" width="48" height="26" rx="4" fill={`url(#w${uid})`} stroke={c.dark} strokeWidth="2.5" />
      <rect x="8" y="38" width="48" height="5" fill={c.band} opacity="0.9" />
      <g className="chest__lid" transform={open ? 'rotate(-28 10 30)' : undefined}>
        <path d="M8 30 V22 C8 14 14 10 22 10 H42 C50 10 56 14 56 22 V30 Z" fill={`url(#w${uid})`} stroke={c.dark} strokeWidth="2.5" />
        <path d="M8 24 H56" stroke={c.band} strokeWidth="4" />
      </g>
      <rect x="27" y="28" width="10" height="13" rx="2.5" fill={c.band} stroke={c.dark} strokeWidth="2" />
      <circle cx="32" cy="34" r="2" fill={c.dark} />
      {open && <path d="M14 26 L50 26" stroke="#fff6dc" strokeWidth="3" opacity="0.8" />}
    </svg>
  );
}

// ── Elements ─────────────────────────────────────────────────────

export const ELEMENT_ICONS: Record<string, LucideIcon> = {
  Neutral: CircleDot,
  Strength: Mountain,
  Intelligence: Flame,
  Chance: Droplet,
  Agility: Wind,
  Multi: Sparkles,
};

export function ElementEmblem({ element, size }: { element: string; size: number }) {
  const Icon = ELEMENT_ICONS[element] ?? CircleDot;
  const color = ELEMENT_COLORS[element] ?? '#9aa0ad';
  return (
    <span className="emblem" style={{ width: size, height: size, ['--emblem' as string]: color }} aria-hidden="true">
      <Icon size={Math.round(size * 0.62)} strokeWidth={2.4} />
    </span>
  );
}

// ── Ornaments ────────────────────────────────────────────────────

/** A laurel sprig framing the player's name; the right one mirrors the left. */
export function OrnamentArt({ seed, side, size }: { seed: string; side: 'left' | 'right'; size: number }) {
  const rand = seededRandom(`ornament:${seed}`);
  const hue = Math.floor(rand() * 360);
  const leaf = `hsl(${hue} 62% 58%)`;
  const edge = `hsl(${hue} 55% 28%)`;
  const leaves = [0, 1, 2, 3].map((i) => {
    const t = 0.18 + i * 0.22;
    const x = 44 - 26 * t;
    const y = 54 - 46 * t + 10 * t * t;
    return { x, y, r: -38 - i * 12 };
  });
  return (
    <svg className="art art--ornament" width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <g transform={side === 'right' ? 'translate(64 0) scale(-1 1)' : undefined}>
        <path d="M46 58 C30 48 22 32 26 8" fill="none" stroke={edge} strokeWidth="3" strokeLinecap="round" />
        {leaves.map((l, i) => (
          <g key={i} transform={`translate(${l.x} ${l.y}) rotate(${l.r})`}>
            <ellipse cx="-7" cy="0" rx="8" ry="3.6" fill={leaf} stroke={edge} strokeWidth="1.4" />
            <ellipse cx="7" cy="0" rx="8" ry="3.6" fill={leaf} stroke={edge} strokeWidth="1.4" opacity="0.85" />
          </g>
        ))}
        <circle cx="26" cy="8" r="4" fill="#f0b44c" stroke={edge} strokeWidth="1.4" />
      </g>
    </svg>
  );
}

// ── Minigames ────────────────────────────────────────────────────

/** A chafer (skeleton) head; the crit one is golden and grinning. */
export function ChaferArt({ crit, size }: { crit: boolean; size: number }) {
  const uid = useId().replace(/:/g, '');
  const [light, dark] = crit ? ['#ffe39a', '#a8670f'] : ['#f2efe6', '#8d877a'];
  return (
    <svg className={`art art--chafer ${crit ? 'is-crit' : ''}`} width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <radialGradient id={`c${uid}`} cx="0.4" cy="0.32" r="0.8">
          <stop offset="0" stopColor="#fff" />
          <stop offset="0.45" stopColor={light} />
          <stop offset="1" stopColor={dark} />
        </radialGradient>
      </defs>
      {crit && <circle cx="32" cy="30" r="29" fill="#f0b44c" opacity="0.22" />}
      <path d="M32 6 C47 6 55 17 55 29 C55 37 51 41 47 43 L47 52 C47 55 45 57 42 57 L22 57 C19 57 17 55 17 52 L17 43 C13 41 9 37 9 29 C9 17 17 6 32 6 Z" fill={`url(#c${uid})`} stroke={dark} strokeWidth="2.5" />
      <ellipse cx="23" cy="30" rx="6.5" ry={crit ? 5.5 : 7} fill="#2a2233" />
      <ellipse cx="41" cy="30" rx="6.5" ry={crit ? 5.5 : 7} fill="#2a2233" />
      {crit && (
        <>
          <circle cx="23" cy="30" r="2.2" fill="#ffdb6e" />
          <circle cx="41" cy="30" r="2.2" fill="#ffdb6e" />
        </>
      )}
      <path d="M29 41 L32 36 L35 41 Z" fill="#2a2233" />
      <path d={crit ? 'M22 47 Q32 54 42 47' : 'M23 48 L41 48'} fill="none" stroke="#2a2233" strokeWidth="2.4" strokeLinecap="round" />
      {[26, 32, 38].map((x) => (
        <line key={x} x1={x} y1="45.5" x2={x} y2="51" stroke="#2a2233" strokeWidth="1.8" />
      ))}
    </svg>
  );
}

/** A racing larva: three body rings and a head in its colour. */
export function LarvaArt({ color, size }: { color: string; size: number }) {
  return (
    <svg className="art art--larva" width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      {[
        { x: 14, y: 40, r: 9 },
        { x: 25, y: 36, r: 10.5 },
        { x: 37, y: 34, r: 11 },
      ].map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={c.r} fill={color} stroke="rgba(0,0,0,0.35)" strokeWidth="2" />
      ))}
      <circle cx="49" cy="30" r="11" fill={color} stroke="rgba(0,0,0,0.35)" strokeWidth="2" />
      <circle cx="46" cy="27" r="3.4" fill="#fff" />
      <circle cx="53" cy="27" r="3.4" fill="#fff" />
      <circle cx="47" cy="27.5" r="1.6" fill="#1d1a24" />
      <circle cx="54" cy="27.5" r="1.6" fill="#1d1a24" />
      <path d="M46 34 Q50 37 54 34" fill="none" stroke="#1d1a24" strokeWidth="1.8" strokeLinecap="round" />
      <ellipse cx="34" cy="28" rx="6" ry="3" fill="#fff" opacity="0.35" />
    </svg>
  );
}
